import React, { useState, useEffect } from 'react';
import { Search, Plus, Trash2, Edit3, X, Play, Clock, Tag, Eye, EyeOff, Save, RefreshCw } from 'lucide-react';
import Field from '../components/Field';
import Pagination from '../components/Pagination';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../context/ToastContext';
import { api } from '../utils/api';

const COURSE_CATEGORIES = [
  'Basic Price Action',
  'Advanced Institutional Strategy',
  'Options (Buying & Selling)',
];

const emptyForm = { title: '', description: '', youtube_id: '', category: '', price: '', validity_days: '180' };

function VideoFormModal({ initial, onClose, onSuccess }: {
  initial?: any; onClose: () => void; onSuccess: () => void;
}) {
  const { toast } = useToast();
  const isEdit = !!initial;
  const [form, setForm] = useState(initial
    ? {
      title: initial.title, description: initial.description, youtube_id: initial.youtube_id,
      category: initial.category, price: String(initial.price), validity_days: String(initial.validity_days)
    }
    : emptyForm
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof form) => (v: string) => {
    // Auto-parse YouTube ID if pasting a link
    if (k === 'youtube_id' && (v.includes('youtube.com') || v.includes('youtu.be'))) {
      const match = v.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]{11})/);
      if (match) v = match[1];
    }
    setForm(p => ({ ...p, [k]: v }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = 'Title is required.';
    if (!form.youtube_id.trim()) e.youtube_id = 'YouTube ID is required.';
    if (!form.category) e.category = 'Please select a category.';
    if (!form.price || +form.price < 0) e.price = 'Enter a valid price.';
    if (!form.validity_days || +form.validity_days < 1) e.validity_days = 'Enter validity days.';
    return e;
  };

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/videos/${initial.id}`, { ...form, price: +form.price, validity_days: +form.validity_days }); }
      else { await api.post('/videos', { ...form, price: +form.price, validity_days: +form.validity_days }); }

      toast(isEdit ? 'Video updated!' : 'Video published!', 'success');
      onSuccess(); onClose();
    } catch (err) {
      toast('Operation failed', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="card w-full max-w-lg p-0 flex flex-col max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Fixed Header */}
        <div className="p-6 border-b border-[var(--glass-border)] flex items-center justify-between shrink-0 bg-[var(--glass-bg)]/50">
          <h2 className="text-lg font-black text-[var(--color-text)]">
            {isEdit ? 'Edit Video' : 'Publish New Video'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-xl text-[var(--color-muted)] hover:text-[var(--color-text)] hover:bg-[var(--glass-bg)] transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="p-6 sm:p-8 overflow-y-auto flex-1 custom-scrollbar">
          <form onSubmit={handle} className="space-y-5">
            <Field label="Title" error={errors.title} required>
              <input className={`input ${errors.title ? 'border-red-400' : ''}`} value={form.title} onChange={e => set('title')(e.target.value)} placeholder="Video title" />
            </Field>
            <Field label="Description">
              <textarea className="input" rows={3} value={form.description} onChange={e => set('description')(e.target.value)} placeholder="Brief description" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="YouTube ID or Link" error={errors.youtube_id} required>
                <input className={`input font-mono ${errors.youtube_id ? 'border-red-400' : ''}`} value={form.youtube_id} onChange={e => set('youtube_id')(e.target.value)} placeholder="e.g. dQw4w9WgXcQ" />
              </Field>
              <Field label="Category" error={errors.category} required>
                <select className={`input ${errors.category ? 'border-red-400' : ''}`} value={form.category} onChange={e => set('category')(e.target.value)}>
                  <option value="">Select category…</option>
                  {COURSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Price (₹)" error={errors.price} required>
                <input type="number" min="0" className={`input ${errors.price ? 'border-red-400' : ''}`} value={form.price} onChange={e => set('price')(e.target.value)} placeholder="1999" />
              </Field>
              <Field label="Validity (days)" error={errors.validity_days} required>
                <input type="number" min="1" className={`input ${errors.validity_days ? 'border-red-400' : ''}`} value={form.validity_days} onChange={e => set('validity_days')(e.target.value)} placeholder="180" />
              </Field>
            </div>
            {form.youtube_id && form.youtube_id.length === 11 && (
              <div className="rounded-2xl overflow-hidden aspect-video mt-2 ring-1 ring-[var(--glass-border)] shadow-lg">
                <img src={`https://img.youtube.com/vi/${form.youtube_id}/hqdefault.jpg`}
                  referrerPolicy="no-referrer" className="w-full h-full object-cover" alt="thumbnail" />
              </div>
            )}

            <div className="flex gap-3 pt-4 sticky bottom-0 bg-[var(--color-bg)]/80 backdrop-blur-md -mx-1 px-1 py-1">
              <button type="submit" disabled={saving} className="btn btn-primary flex-1 py-3 disabled:opacity-60 shadow-lg shadow-[var(--color-accent)]/20">
                <Save className="w-4 h-4" /> {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Publish Video'}
              </button>
              <button type="button" onClick={onClose} className="btn btn-ghost px-6">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdminVideos() {
  const [videos, setVideos] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; title: string } | null>(null);
  const { toast } = useToast();

  const fetchVideos = (page = 1) => {
    setLoading(true);
    api.get(`/videos/admin?page=${page}&limit=5&search=${search}`)
      .then((res: any) => {
        setVideos(res.data || []);
        setPagination(res.pagination);
      })
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchVideos(1); }, []);
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchVideos(1); // Fetch from page 1 when search changes
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  const handleDelete = async (id: number, title: string) => {
    setConfirmDelete({ id, title });
  };

  const executeDelete = async () => {
    if (!confirmDelete) return;
    await api.delete(`/videos/${confirmDelete.id}`);
    toast('Video deleted.', 'info');
    setConfirmDelete(null);
    fetchVideos(pagination.page);
  };

  return (
    <>
      {confirmDelete && (
        <ConfirmModal
          title="Are you sure?"
          message={`You are about to delete "${confirmDelete.title}". This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={executeDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {(showAdd || editing) && (
        <VideoFormModal
          initial={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSuccess={() => fetchVideos(pagination.page)}
        />
      )}
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-black text-[var(--color-text)] tracking-tight">Video Management</h1>
            <p className="text-sm text-[var(--color-muted)]">
              Manage all your published videos.
            </p>
          </div>
          <div className="flex gap-2 self-start sm:self-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search videos..."
                className="input !pl-[30px] pr-3 py-2 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-muted)]" />
            </div>
            <button onClick={() => setShowAdd(true)} className="btn btn-primary btn-sm">
              <Plus className="w-3.5 h-3.5" /> Add Video
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-[var(--color-muted)]">Loading videos…</div>
        ) : (
          <div className="card overflow-hidden">
            <div className="p-4 sm:p-0">
              {/* Mobile card layout */}
              <div className="sm:hidden space-y-3">
                {videos.length === 0 ? (
                  <div className="text-center text-sm text-[var(--color-muted)] py-8">
                    {search ? 'No videos found matching your search.' : 'No videos published yet.'}
                  </div>
                ) : (
                  videos.map((v: any) => (
                    <div key={v.id} className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)]">
                      <div className="flex gap-3 mb-3">
                        <div className="w-24 aspect-video rounded-lg overflow-hidden bg-[var(--glass-bg)] relative shrink-0">
                          <img src={`https://img.youtube.com/vi/${v.youtube_id}/default.jpg`}
                            referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-[var(--color-text)] line-clamp-2">{v.title}</p>
                          <p className="text-[10px] text-[var(--color-muted)] font-mono flex items-center gap-1 mt-1">
                            <Play className="w-2.5 h-2.5" /> {v.youtube_id}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="badge badge-gray flex items-center gap-1 text-[10px]">
                            <Tag className="w-2.5 h-2.5" /> {v.category}
                          </span>
                          <span className="text-xs font-bold" style={{ color: 'var(--color-accent)' }}>₹{v.price}</span>
                          <span className="text-[10px] text-[var(--color-muted)] flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5" /> {v.validity_days}d
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setEditing(v)}
                            className="btn btn-icon btn-ghost" title="Edit">
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDelete(v.id, v.title)}
                            className="btn btn-icon btn-danger" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="tbl">
                  <thead>
                    <tr><th>Thumbnail</th><th>Title</th><th>Category</th><th>Price</th><th>Validity</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {videos.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-sm text-[var(--color-muted)] py-8">
                          {search ? 'No videos found matching your search.' : 'No videos published yet.'}
                        </td>
                      </tr>
                    ) : (
                      videos.map((v: any) => (
                        <tr key={v.id}>
                          <td>
                            <div className="w-14 aspect-video rounded-lg overflow-hidden bg-[var(--glass-bg)] relative">
                              <img src={`https://img.youtube.com/vi/${v.youtube_id}/default.jpg`}
                                referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                            </div>
                          </td>
                          <td>
                            <p className="text-sm font-bold max-w-[200px] truncate">{v.title}</p>
                            <p className="text-[10px] text-[var(--color-muted)] font-mono flex items-center gap-1">
                              <Play className="w-3 h-3" /> {v.youtube_id}
                            </p>
                          </td>
                          <td>
                            <span className="badge badge-gray flex items-center gap-1">
                              <Tag className="w-3 h-3" /> {v.category}
                            </span>
                          </td>
                          <td className="font-bold" style={{ color: 'var(--color-accent)' }}>₹{v.price}</td>
                          <td className="text-[var(--color-muted)]">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {v.validity_days}d
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => setEditing(v)}
                                className="btn btn-icon btn-ghost" title="Edit">
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleDelete(v.id, v.title)}
                                className="btn btn-icon btn-danger" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={fetchVideos}
      />
    </>
  );
}
